import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVisiteByCliente, useVisiteByOfferta, useUpdateVisita } from '@offertify/query';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Plus,
  MapPin,
  Calendar,
  CalendarPlus,
  Clock,
  Loader2,
  ChevronDown,
  CheckCircle,
  XCircle,
  RotateCcw,
  FileText,
  Paperclip,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { leadsAPI, visiteAPI } from '../../utils/api';
import { useActivityTypes, resolveActivityType } from '../../hooks/useActivityTypes';
import LeadActivityTimeline from '../lead/LeadActivityTimeline';
import LeadQuickActivityForm from '../lead/LeadQuickActivityForm';
import AttivitaForm, { AttivitaContext } from './AttivitaForm';
import AttivitaAllegati from './AttivitaAllegati';
import type { Visita, VisitaStato } from '../../types';

const STATO_CONFIG: Record<VisitaStato, { label: string; icon: React.ReactNode; color: string }> = {
  programmata: { label: 'Programmata', icon: <Calendar className="h-4 w-4" />, color: 'bg-yellow-100 text-yellow-700' },
  completata: { label: 'Completata', icon: <CheckCircle className="h-4 w-4" />, color: 'bg-green-100 text-green-700' },
  annullata: { label: 'Annullata', icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-700' },
  rinviata: { label: 'Rinviata', icon: <RotateCcw className="h-4 w-4" />, color: 'bg-orange-100 text-orange-700' },
};

interface AttivitaTabProps {
  context: AttivitaContext;
}

/** Wrapper lista + form riusato dai tre dettagli (lead / cliente / offerta). */
export default function AttivitaTab({ context }: AttivitaTabProps) {
  if (context.kind === 'lead') {
    return <LeadAttivitaTab leadId={context.id} />;
  }
  return <VisiteAttivitaTab context={context} />;
}

// --- Lead: timeline + quick form (stessa disposizione di LeadDetail) ---
function LeadAttivitaTab({ leadId }: { leadId: string }) {
  const { data: activitiesData, isLoading } = useQuery({
    queryKey: ['lead-activities', leadId],
    queryFn: async () => (await leadsAPI.listActivities(leadId, { page: 1, limit: 100 })).data,
    enabled: !!leadId,
  });
  const activities = activitiesData?.data || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline attività</h3>
        <LeadActivityTimeline activities={activities} isLoading={isLoading} />
      </div>
      <div>
        <LeadQuickActivityForm leadId={leadId} />
      </div>
    </div>
  );
}

// --- Cliente/Offerta: lista + filtro + paginazione + form (era ClientVisiteTab) ---
function VisiteAttivitaTab({ context }: { context: { kind: 'cliente' | 'offerta'; id: string } }) {
  const [page, setPage] = useState(1);
  const [filterStato, setFilterStato] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingVisita, setEditingVisita] = useState<Visita | null>(null);
  const limit = 10;

  const updateVisita = useUpdateVisita();
  const { data: types } = useActivityTypes(context.kind);

  // Entrambi gli hook vanno chiamati sempre (regole degli hook): quello del
  // contesto non attivo resta disabilitato via `enabled: !!id`.
  const clienteQuery = useVisiteByCliente(
    context.kind === 'cliente' ? context.id : undefined,
    { page, limit, stato: filterStato || undefined },
  );
  const offertaQuery = useVisiteByOfferta(
    context.kind === 'offerta' ? context.id : undefined,
    { page, limit, stato: filterStato || undefined },
  );
  const { data, isLoading } = context.kind === 'cliente' ? clienteQuery : offertaQuery;

  const visite = data?.data || [];
  const pagination = data?.pagination;
  const hasMore = pagination ? page < pagination.pages : false;
  const emptyStateLabel = context.kind === 'cliente'
    ? 'Nessuna attività registrata per questo cliente'
    : "Nessuna attività registrata per quest'offerta";

  if (isLoading && page === 1) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filter and create button */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <select
            value={filterStato}
            onChange={(e) => { setFilterStato(e.target.value); setPage(1); }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="programmata">Programmate</option>
            <option value="completata">Completate</option>
            <option value="annullata">Annullate</option>
            <option value="rinviata">Rinviate</option>
          </select>
          {pagination && (
            <span className="text-sm text-gray-500">{pagination.total} risultati</span>
          )}
        </div>
        <button
          onClick={() => { setShowCreateForm(true); setEditingVisita(null); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuova attività
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showCreateForm || editingVisita) && (
        <AttivitaForm
          context={context}
          visita={editingVisita}
          onClose={() => { setShowCreateForm(false); setEditingVisita(null); }}
          onSuccess={() => {
            setShowCreateForm(false);
            setEditingVisita(null);
          }}
        />
      )}

      {/* Empty state */}
      {visite.length === 0 && !showCreateForm && (
        <div className="text-center py-12">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">{emptyStateLabel}</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            Pianifica la prima attività
          </button>
        </div>
      )}

      {/* Visits list */}
      {visite.length > 0 && (
        <div className="space-y-3">
          {visite.map((visita) => (
            <VisitaCard
              key={visita.id}
              visita={visita}
              types={types}
              onEdit={() => { setEditingVisita(visita); setShowCreateForm(false); }}
              onStatusChange={async (stato: VisitaStato) => {
                await updateVisita.mutateAsync({ id: visita.id, data: { stato } });
              }}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Precedente
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-500">
            Pagina {page} di {pagination.pages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={!hasMore}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Successiva
            <ChevronDown className="h-3 w-3 -rotate-90" />
          </button>
        </div>
      )}
    </div>
  );
}

// --- Visita Card ---
function VisitaCard({
  visita,
  types,
  onEdit,
  onStatusChange,
}: {
  visita: Visita;
  types: ReturnType<typeof useActivityTypes>['data'];
  onEdit: () => void;
  onStatusChange: (stato: VisitaStato) => Promise<void>;
}) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDownloadingIcs, setIsDownloadingIcs] = useState(false);
  const [showAllegati, setShowAllegati] = useState(false);
  const tipo = resolveActivityType(visita.tipo, types);
  const TipoIcon = tipo.icon;
  const stato = STATO_CONFIG[visita.stato] || STATO_CONFIG.programmata;

  const handleStatusChange = async (newStato: VisitaStato) => {
    setIsUpdating(true);
    try {
      await onStatusChange(newStato);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownloadIcs = async () => {
    setIsDownloadingIcs(true);
    try {
      const res = await visiteAPI.downloadIcs(visita.id);
      const blob = new Blob([res.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visita-${visita.id}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nel download del calendario');
    } finally {
      setIsDownloadingIcs(false);
    }
  };

  const dataFormatted = (() => {
    try {
      return format(parseISO(visita.data), 'EEEE d MMMM yyyy', { locale: it });
    } catch {
      return visita.data;
    }
  })();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Type icon */}
          <div
            className="flex-shrink-0 p-2 rounded-lg"
            style={{ backgroundColor: `${tipo.colore}1A`, color: tipo.colore }}
          >
            <TipoIcon className="h-4 w-4" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Title row */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900">{tipo.label}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${stato.color}`}>
                {stato.icon}
                {stato.label}
              </span>
            </div>

            {/* Date & time */}
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {dataFormatted}
              </span>
              {visita.ora && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {visita.ora.substring(0, 5)}
                </span>
              )}
            </div>

            {/* Address */}
            {visita.indirizzo && (
              <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{visita.indirizzo}</span>
              </div>
            )}

            {/* Notes */}
            {visita.note && (
              <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">
                <div className="flex items-start gap-1">
                  <FileText className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
                  <span>{visita.note}</span>
                </div>
              </div>
            )}

            {/* Esito (outcome) */}
            {visita.esito && (
              <div className="mt-2 text-sm text-green-700 bg-green-50 rounded p-2">
                <span className="font-medium">Esito: </span>{visita.esito}
              </div>
            )}

            {/* Follow-up */}
            {visita.follow_up_date && (
              <div className="mt-2 text-sm text-blue-700 bg-blue-50 rounded p-2">
                <span className="font-medium">Follow-up: </span>
                {(() => {
                  try {
                    return format(parseISO(visita.follow_up_date), 'd MMMM yyyy', { locale: it });
                  } catch {
                    return visita.follow_up_date;
                  }
                })()}
                {visita.follow_up_note && <span> - {visita.follow_up_note}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {visita.stato === 'programmata' && (
            <>
              <button
                onClick={() => handleStatusChange('completata')}
                disabled={isUpdating}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Segna come completata"
              >
                <CheckCircle className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStatusChange('rinviata')}
                disabled={isUpdating}
                className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                title="Rinvia"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStatusChange('annullata')}
                disabled={isUpdating}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Annulla"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </>
          )}
          <button
            onClick={handleDownloadIcs}
            disabled={isDownloadingIcs}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
            title="Aggiungi al calendario"
          >
            {isDownloadingIcs ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CalendarPlus className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setShowAllegati((v) => !v)}
            className={`p-1.5 rounded-lg transition-colors ${
              showAllegati ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
            title="Foto e documenti"
            aria-expanded={showAllegati}
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            title="Modifica"
          >
            <FileText className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showAllegati && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <AttivitaAllegati visitaId={visita.id} />
        </div>
      )}
    </div>
  );
}
